import { FC, useState } from "react"
import { EyeOff, AlertTriangle } from "lucide-react"
import { PrimaryBtn } from "../../buttons"
import Dialog from "../../dialog"
import anonymizeEmail, {
  AnonymizeEmailResponse
} from "../../../../api/anonymize-email"
import { useAppDispatch } from "../../../hooks"
import { pushToast } from "../../../../redux/slices/notifications"

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const AnonymizeEmail: FC = () => {
  const dispatch = useAppDispatch()
  const [email, setEmail] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [result, setResult] = useState<AnonymizeEmailResponse | null>(null)

  const isValid = EMAIL_RE.test(email)

  const handleAnonymize = async () => {
    setShowConfirm(false)
    setIsLoading(true)
    setResult(null)

    try {
      const res = await anonymizeEmail.anonymize({ email })
      setResult(res)
      dispatch(
        pushToast({
          msg: res.message || "Email records anonymized successfully",
          theme: "success",
          timeout: 4000
        })
      )
      setEmail("")
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to anonymize email"
      dispatch(pushToast({ msg, theme: "danger", timeout: 5000 }))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-white border border-neutral-200 rounded-lg">
      <div className="px-6 py-5 border-b border-neutral-100 bg-neutral-50 rounded-t-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
            <EyeOff className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Anonymize Email
            </h3>
            <p className="text-sm text-gray-500">
              Anonymize all records associated with an email address
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">This action is irreversible</p>
            <p className="mt-1 text-amber-700">
              All leads, subscriptions, and pending subscriptions for this email
              will be permanently anonymized.
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address:
          </label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="user@example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="pt-2">
          <PrimaryBtn
            onClick={() => setShowConfirm(true)}
            disabled={!isValid || isLoading}
            className="w-full justify-center"
          >
            {isLoading ? "Anonymizing..." : "Anonymize Records"}
          </PrimaryBtn>
        </div>

        {result && (
          <div className="border border-neutral-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-neutral-200">
              <p className="text-sm font-medium text-gray-900">
                {result.message}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4 p-4 text-center">
              <div>
                <p className="text-lg font-semibold text-gray-900">
                  {result.leads_anonymized}
                </p>
                <p className="text-xs text-gray-500">Leads</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900">
                  {result.subscriptions_anonymized}
                </p>
                <p className="text-xs text-gray-500">Subscriptions</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900">
                  {result.pending_subscriptions_anonymized}
                </p>
                <p className="text-xs text-gray-500">Pending</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog
        open={showConfirm}
        title="Confirm Email Anonymization"
        body={`This will permanently anonymize all leads, subscriptions, and pending subscriptions for "${email}". This cannot be undone.`}
        okLabel="Yes, Anonymize"
        cancelLabel="Cancel"
        onOk={handleAnonymize}
        onCancel={() => setShowConfirm(false)}
        onClose={() => setShowConfirm(false)}
        theme="warning"
      />
    </div>
  )
}

export default AnonymizeEmail
